import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, CreditCard, CheckCircle2 } from "lucide-react";
import api, { setAuthToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AccountSetup({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const quoteId = params.get("quote") || "";
  const quoteNumber = params.get("n") || "";
  const quoteTotal = params.get("t") || "";
  const quoteCurrency = params.get("c") || "EUR";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  const money = (v, c) => v === "" || v == null ? "" : new Intl.NumberFormat("it-IT", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(Number(v));

  useEffect(() => {
    if (!token) { setError("Link mancante"); return; }
    api.get(`/auth/setup-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  useEffect(() => {
    if (mode === "magic" && token) {
      api.post("/auth/magic-login", { token })
        .then(async ({ data }) => { setAuthToken(data.token); await refresh(); toast.success("Accesso effettuato"); navigate("/dashboard"); })
        .catch((e) => setError(formatApiError(e.response?.data?.detail)));
    }
  }, [mode, token]);

  const submitPassword = async () => {
    if (password.length < 6) { toast.error("Password troppo corta (min 6)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/setup-password", { token, password });
      setAuthToken(data.token); await refresh();
      toast.success("Password impostata");
      if (quoteId) { setPasswordSet(true); }  // stay to offer immediate payment
      else navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const payNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/quote/${quoteId}/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
      else if (data.already_paid) { toast.success("Già pagato"); navigate("/dashboard"); }
      else navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const QuoteSummary = () => quoteId ? (
    <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50 px-5 py-4 text-center" data-testid="setup-quote-summary">
      <span className="text-xs uppercase tracking-wider text-stone-400">Preventivo {quoteNumber}</span>
      {quoteTotal && <div className="text-2xl font-semibold text-blue-900 mt-1">{money(quoteTotal, quoteCurrency)}</div>}
    </div>
  ) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="account-setup-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        {error ? (
          <div className="text-center" data-testid="setup-error">
            <p className="text-rose-600 font-medium">{error}</p>
            <Link to="/login" className="text-blue-700 text-sm mt-4 inline-block hover:underline">Vai al login</Link>
          </div>
        ) : mode === "magic" ? (
          <div className="text-center text-slate-500" data-testid="magic-loading">
            <KeyRound className="mx-auto mb-3 text-blue-900" size={28} /> Accesso in corso…
          </div>
        ) : passwordSet ? (
          <div className="text-center" data-testid="setup-paynow">
            <CheckCircle2 className="mx-auto mb-3 text-emerald-500" size={36} />
            <h1 className="font-display text-2xl font-bold text-slate-900">Account attivato</h1>
            <p className="text-sm text-slate-500 mt-1">Puoi pagare subito il tuo preventivo o farlo più tardi dalla dashboard.</p>
            <QuoteSummary />
            <div className="mt-6 space-y-2.5">
              <Button onClick={payNow} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12" data-testid="setup-pay-now">
                <CreditCard size={17} className="mr-2" /> Paga adesso
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full h-11" data-testid="setup-go-dashboard">
                Vai alla dashboard
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ShieldCheck className="mx-auto mb-3 text-blue-900" size={28} />
            <h1 className="font-display text-2xl font-bold text-center text-slate-900">Imposta la tua password</h1>
            {info && <p className="text-center text-sm text-slate-500 mt-1">{info.email}</p>}
            <QuoteSummary />
            <div className="space-y-3 mt-6">
              <div className="space-y-1.5">
                <Label>Nuova password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitPassword()} data-testid="setup-password-input" />
              </div>
              <Button onClick={submitPassword} disabled={busy || !info} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="setup-password-submit">
                {busy ? "..." : "Imposta password e accedi"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
